import { useCallback, useEffect, useRef } from "react";

/**
 * Typing counted once it stops: `settle(text)` from the field's change
 * handler, and `fire` runs when nothing more is typed for `delayMs`. Empty
 * text, or the text it last fired for, fires nothing, so clearing or retyping
 * counts once per mount. The text stays in this closure; `fire` decides what
 * of it, if anything, is sent.
 *
 * Kept out of `analytics.ts`, which every page test replaces with mocks.
 */
export function useSettled(fire: (text: string) => void, delayMs = 1000): (text: string) => void {
  const latest = useRef(fire);
  latest.current = fire;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fired = useRef<string | null>(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  return useCallback((text: string) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const settled = text.trim();
      if (!settled || settled === fired.current) return;
      fired.current = settled;
      latest.current(text);
    }, delayMs);
  }, [delayMs]);
}
